import type { ScaleTime } from 'd3-scale';
import { isNaN } from 'lodash-es';

import { OVERLAY_PADDING } from '../../../../../core/components/data-point/constants.js';
import { ColorToken } from '../../../../../core/types/color-palette.js';
import { getClusterBoundariesAtPosition } from '../../../../../core/utils/binary-search.js';
import { safeGet } from '../../../../../core/utils/safe-get.js';
import { sanitizeZeroValues } from '../../../../../core/utils/sanitize-zero-values.js';
import { YAxisScales } from '../../../../types/state.js';
import { WeakMapDatapoints } from '../../../../types/timeseries.js';
import { getDatapointTime } from '../../../../utils/timeseries.js';
import { getYScaleByUnit } from '../../../../utils/yAxis.js';
import { TimeseriesDataPointAllTypes } from '../../../displays/shared/types.js';

export type TimeseriesClosestPoint = {
  id: string;
  x: number;
  y: number;
  datapoint: TimeseriesDataPointAllTypes;
  color: ColorToken;
};

export const getClosestPoint = (
  xPoints: TimeseriesClosestPoint[],
  mouseY: number = 0,
) => {
  let aux = Infinity;
  let auxPoint = xPoints[0];
  // new object needed to preserve the points sorting
  xPoints.forEach((elem) => {
    if (Math.abs(elem.y - mouseY) < aux) {
      aux = Math.abs(elem.y - mouseY);
      auxPoint = elem;
    }
  });
  return auxPoint;
};

function xAccessor(xAxisScale: ScaleTime<number, number>) {
  return (datapoint: TimeseriesDataPointAllTypes) =>
    xAxisScale(getDatapointTime(datapoint.start, datapoint.end));
}

function roundTo2Decimals(num: number): number {
  const factor = 100;
  return Math.round(num * factor) / factor;
}

function clusterAccessor(
  xAxisScale: ScaleTime<number, number>,
  mouseX: number,
) {
  return (datapoint: TimeseriesDataPointAllTypes) =>
    roundTo2Decimals(
      Math.abs(
        xAxisScale(getDatapointTime(datapoint.start, datapoint.end)) - mouseX,
      ),
    );
}

function mapToClosestPoints(
  datapointsMap: WeakMapDatapoints,
  xAxisScale: ScaleTime<number, number>,
  yAxis: YAxisScales,
) {
  return (datapoint: TimeseriesDataPointAllTypes): TimeseriesClosestPoint[] => {
    const datapointMap = safeGet(datapointsMap, datapoint);
    const {
      isGap,
      color,
      value,
      unit,
      y0,
      y1,
      cumulativeValue,
      seriesId,
      start,
      end,
      center,
      variant,
    } = datapointMap;

    if (isGap) {
      return [];
    }

    const yScale = getYScaleByUnit(unit, yAxis);
    const x = xAxisScale(getDatapointTime(start, end, center)) as number;

    if (variant === 'band') {
      const pointY0 = sanitizeZeroValues(yScale, y0 ?? 0);
      const pointY1 = sanitizeZeroValues(yScale, y1 ?? 0);

      return [
        { id: seriesId, x, y: pointY0, color, datapoint },
        { id: seriesId, x, y: pointY1, color, datapoint },
      ];
    }

    const y = sanitizeZeroValues(
      yScale,
      cumulativeValue ? cumulativeValue : value ?? 0,
    );

    if (!isNaN(y)) {
      return [{ id: seriesId, x, y, color, datapoint }];
    }

    return [];
  };
}

/**
 * Returns the closest points sorted by proximity given a series and mouse
 * coordinates within the same X coordinate as the closest point.
 */
export const findClosestPoints = (
  mouseX: number,
  datapoints: TimeseriesDataPointAllTypes[],
  xAxisScale: ScaleTime<number, number>,
  yAxis: YAxisScales,
  datapointsMap: WeakMapDatapoints,
): TimeseriesClosestPoint[] => {
  const [leftIndex, rightIndex] = getClusterBoundariesAtPosition(
    mouseX,
    datapoints,
    xAccessor(xAxisScale),
    clusterAccessor(xAxisScale, mouseX),
  );

  const toClosestPoint = mapToClosestPoints(datapointsMap, xAxisScale, yAxis);

  const closestPoints: TimeseriesClosestPoint[] = [];
  for (let i = leftIndex; i <= rightIndex; i++) {
    closestPoints.push(...toClosestPoint(datapoints[i]));
  }

  return closestPoints;
};
/**
 * Filters the data points that would fall outside the charts, but inside the overlays area.
 */
export const filterOutOfBounds =
  (overlaysWidth: number, overlaysHeight: number) =>
  (datapoint: TimeseriesClosestPoint) => {
    return (
      datapoint.y >= OVERLAY_PADDING &&
      datapoint.y <= overlaysHeight + OVERLAY_PADDING &&
      datapoint.x >= OVERLAY_PADDING &&
      datapoint.x <= overlaysWidth + OVERLAY_PADDING
    );
  };
